import { environment } from 'src/environments/environment';
import { isPreviewHost, isSharedHost } from './host-classification.util';

interface FittaraPreviewRuntimeConfig {
  storefrontDomain?: string;
}

declare global {
  interface Window {
    __FITTARA_PREVIEW_RUNTIME_CONFIG__?: Readonly<FittaraPreviewRuntimeConfig>;
  }
}

const dnsHostnamePattern = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

function getPreviewStorefrontDomain(currentDomain: string): string | null {
  if (!isPreviewHost(currentDomain) || typeof window === 'undefined') {
    return null;
  }

  const configuredDomain = window.__FITTARA_PREVIEW_RUNTIME_CONFIG__?.storefrontDomain
    ?.trim()
    .toLowerCase()
    .replace(/\.$/, '');

  if (!configuredDomain
    || !dnsHostnamePattern.test(configuredDomain)
    || isLocalhostOrIp(configuredDomain)
    || isSharedHost(configuredDomain)) {
    return null;
  }

  return configuredDomain;
}

/**
 * Determines whether the supplied domain represents localhost or an IP address.
 */
export function isLocalhostOrIp(domain: string): boolean {
  if (!domain) {
    return true;
  }

  const normalized = domain.toLowerCase().trim();
  if (!normalized) {
    return true;
  }

  const localhosts = ['localhost', '127.0.0.1', '0.0.0.0', '::1'];
  if (localhosts.includes(normalized)) {
    return true;
  }

  const ipPattern = /^\d{1,3}(\.\d{1,3}){3}$/;
  return ipPattern.test(normalized);
}

/**
 * Resolves the shop domain that should be used to look up shop metadata.
 * Falls back to the configured default when the current hostname is localhost, an IP, empty, or a
 * shared host. Preview hosts are exempt from the fallback — they are shared-host-prefixed but have
 * their own domain registered in the per-preview database.
 */
export function resolveShopDomain(hostname: string | null | undefined): string | null {
  const rawHostname = hostname ?? '';
  const domain = rawHostname.split(':')[0].trim().toLowerCase();
  const fallback = environment.defaultShopDomain?.trim();
  const previewStorefrontDomain = getPreviewStorefrontDomain(domain);

  if (previewStorefrontDomain) {
    return previewStorefrontDomain;
  }

  if (!domain || isLocalhostOrIp(domain) || (isSharedHost(domain) && !isPreviewHost(domain))) {
    return fallback ?? (domain || null);
  }

  return domain;
}
